import axios from "axios";
import { toast } from "sonner";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

export const TOKEN_STORAGE_KEY = "prosper360.token";
export const USER_STORAGE_KEY = "prosper360.user";

// Storage may throw in incognito/privacy modes or when the disk is full.
// We surface errors to the console but keep the app functional (return null
// / no-op) so the UI does not crash. Tokens live only in the browser tab;
// stealing them would require an XSS foothold, which we mitigate via CSP
// and by rendering only sanitized content on the UI.

// Only log storage errors when running outside of production so we do not
// leak debug information (or noise) in end-user consoles.
const isDev = () => {
    try {
        return process.env.NODE_ENV !== "production";
    } catch (_e) {
        return false;
    }
};
function logStorageWarning(context, err) {
    if (!isDev()) return;
    // eslint-disable-next-line no-console
    console.warn(`[api] ${context} failed:`, err);
}

export function getStoredToken() {
    try {
        return localStorage.getItem(TOKEN_STORAGE_KEY);
    } catch (err) {
        logStorageWarning("getStoredToken", err);
        return null;
    }
}

export function setStoredToken(token) {
    try {
        if (token) localStorage.setItem(TOKEN_STORAGE_KEY, token);
        else localStorage.removeItem(TOKEN_STORAGE_KEY);
    } catch (err) {
        logStorageWarning("setStoredToken", err);
    }
}

export function getStoredUser() {
    try {
        const raw = localStorage.getItem(USER_STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (err) {
        logStorageWarning("getStoredUser", err);
        return null;
    }
}

export function setStoredUser(user) {
    try {
        if (user) localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
        else localStorage.removeItem(USER_STORAGE_KEY);
    } catch (err) {
        logStorageWarning("setStoredUser", err);
    }
}

const api = axios.create({
    baseURL: `${backendUrl}/api`,
    timeout: 20000,
});

api.interceptors.request.use((config) => {
    const token = getStoredToken();
    if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

api.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error?.response?.status;
        const detail = error?.response?.data?.detail;
        if (status === 401) {
            // Only auto-logout if we had a token (avoid loops on login errors)
            const hadToken = !!getStoredToken();
            if (hadToken) {
                setStoredToken(null);
                setStoredUser(null);
                if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
                    window.location.href = "/login";
                }
            }
        }
        // Do not toast here for 401/422 on submit; caller decides.
        if (typeof detail === "string" && status && status >= 500) {
            toast.error("Erro no servidor. Tente novamente.");
        }
        return Promise.reject(error);
    },
);

export function getErrorMessage(error, fallback = "Ocorreu um erro.") {
    const detail = error?.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
    if (error?.message) return error.message;
    return fallback;
}

export default api;
